import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { INSTRUCTIONS_CHART_BUNDLE_URL } from '@constants/instructionsChartBundle';
import type {
  InstructionsChartBundlePayload,
  InstructionsChartBundleReadyMessage,
} from '@app-types/instructions';

export interface InstructionsImpactChartFrameProps {
  payload: InstructionsChartBundlePayload;
  className?: string;
}

const isBundleReadyMessage = (
  value: unknown,
): value is InstructionsChartBundleReadyMessage => {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return false;
  }

  const candidate = value as Record<string, unknown>;
  return candidate.source === 'labci-chart5' && candidate.message === 'ready';
};

const parseBundleMessage = (value: unknown): unknown => {
  if (typeof value !== 'string' || !value) return null;

  try {
    return JSON.parse(value) as unknown;
  } catch {
    return null;
  }
};

/**
 * Same-origin bridge for the supplied Chart5 artifact. This channel is
 * internal to eSmart and deliberately separate from the ASP parent messages.
 */
const InstructionsImpactChartFrame: React.FC<InstructionsImpactChartFrameProps> = ({
  payload,
  className,
}) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const lastBaseSignatureRef = useRef<string | null>(null);
  const lastEventSignatureRef = useRef<string | null>(null);
  const [readyVersion, setReadyVersion] = useState(0);
  const bundleLocation = useMemo(
    () => new URL(INSTRUCTIONS_CHART_BUNDLE_URL, window.location.href),
    [],
  );
  const baseSignature = useMemo(() => {
    const { eventData: _eventData, ...basePayload } = payload;
    return JSON.stringify(basePayload);
  }, [payload]);
  const eventSignature = useMemo(
    () => JSON.stringify(payload.eventData),
    [payload.eventData],
  );

  useLayoutEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.source !== iframeRef.current?.contentWindow) return;
      if (event.origin !== bundleLocation.origin) return;
      if (!isBundleReadyMessage(parseBundleMessage(event.data))) return;

      // Every ready event represents a fresh child lifecycle. Reset message
      // history so an iframe reload always receives a complete payload.
      lastBaseSignatureRef.current = null;
      lastEventSignatureRef.current = null;
      setReadyVersion((version) => version + 1);
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [bundleLocation.origin]);

  useEffect(() => {
    if (readyVersion === 0) return;

    const frameWindow = iframeRef.current?.contentWindow;
    if (!frameWindow) return;

    if (lastBaseSignatureRef.current !== baseSignature) {
      frameWindow.postMessage(JSON.stringify(payload), bundleLocation.origin);
      lastBaseSignatureRef.current = baseSignature;
      lastEventSignatureRef.current = eventSignature;
      return;
    }

    if (lastEventSignatureRef.current !== eventSignature) {
      frameWindow.postMessage(JSON.stringify({
        source: 'labci-chart5',
        eventData: payload.eventData,
      }), bundleLocation.origin);
      lastEventSignatureRef.current = eventSignature;
    }
  }, [baseSignature, bundleLocation.origin, eventSignature, payload, readyVersion]);

  return (
    <iframe
      ref={iframeRef}
      className={[
        'instructions-impact-chart-frame',
        className ?? '',
      ].filter(Boolean).join(' ')}
      src={INSTRUCTIONS_CHART_BUNDLE_URL}
      title="適時開示影響チャート"
      referrerPolicy="no-referrer"
    />
  );
};

export default InstructionsImpactChartFrame;
